import { useCallback, useEffect, useRef } from "react";

import { registerDismissable } from "./dismissableRegistry";

/*
 * Scroll lock is process-wide, not per-hook: two stacked dialogs must not each
 * stash and restore their own "previous" overflow value, or the inner one's
 * close would hand scrolling back while the outer dialog is still up.
 */
let scrollLockCount = 0;
let scrollLockRestore: string | null = null;

function lockPageScroll(): void {
  if (scrollLockCount === 0) {
    scrollLockRestore = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
  }
  scrollLockCount += 1;
}

function unlockPageScroll(): void {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.documentElement.style.overflow = scrollLockRestore ?? "";
    scrollLockRestore = null;
  }
}

export interface UseDialogOptions {
  /** React owns the truth; the hook mirrors it onto showModal/close. */
  open: boolean;
  /** Called once per user-driven close (Escape, backdrop, form close). */
  onClose: () => void;
  /** When false, Escape and backdrop clicks are ignored. Defaults to true. */
  dismissable?: boolean;
}

export interface UseDialogResult {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  handleCancel: (event: React.SyntheticEvent<HTMLDialogElement>) => void;
  handleClose: (event: React.SyntheticEvent<HTMLDialogElement>) => void;
}

/**
 * Drives a native modal `<dialog>` from React state: opens and closes it, honours
 * Escape and backdrop clicks when dismissable, locks page scroll while open, and
 * reports every close back through `onClose`.
 *
 * Attach `dialogRef` to the `<dialog>`. The returned `handleCancel`/`handleClose`
 * are optional - the hook also listens natively, and a close is reported once
 * whether you wire them as `onCancel`/`onClose` or not.
 */
export function useDialog({
  open,
  onClose,
  dismissable = true,
}: UseDialogOptions): UseDialogResult {
  const dialogRef = useRef<HTMLDialogElement>(null);

  /* Count this dialog while it is up, so a host's global Escape handling knows
     an Escape landing here belongs to the dialog. Registered even when it is
     not dismissable: such a dialog swallows Escape rather than closing on it,
     which is just as much a reason for that press to mean nothing else. */
  useEffect(() => {
    if (!open) return undefined;
    return registerDismissable();
  }, [open]);

  const onCloseRef = useRef(onClose);
  const dismissableRef = useRef(dismissable);
  useEffect(() => {
    onCloseRef.current = onClose;
    dismissableRef.current = dismissable;
  });

  /* One native `close` event can reach us twice (our listener plus a consumer
     wiring handleClose). Keying on the event instance collapses that to one. */
  const handledEventRef = useRef<Event | null>(null);
  /* Set when we close the dialog ourselves because `open` went false: React
     already knows, so that close must not bounce back through onClose. The
     `close` event is task-queued, hence a ref rather than a local flag. */
  const suppressNextCloseRef = useRef(false);

  const runClose = useCallback((nativeEvent: Event) => {
    if (handledEventRef.current === nativeEvent) return;
    handledEventRef.current = nativeEvent;
    if (suppressNextCloseRef.current) {
      suppressNextCloseRef.current = false;
      return;
    }
    onCloseRef.current();
  }, []);

  const runCancel = useCallback((nativeEvent: Event) => {
    if (!dismissableRef.current) nativeEvent.preventDefault();
  }, []);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) =>
      runCancel(event.nativeEvent),
    [runCancel],
  );

  const handleClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) =>
      runClose(event.nativeEvent),
    [runClose],
  );

  /* `open` is a dependency because consumers may render the <dialog> only while
     open, so the element can appear after the mount commit. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeCancel = (event: Event) => runCancel(event);
    const handleNativeClose = (event: Event) => runClose(event);

    /* Backdrop dismiss: a press on the backdrop (or the dialog's own padding)
       targets the dialog element itself, so anything that both targets the
       dialog and lands outside its content child is a dismiss. pointerdown, not
       click, so a selection drag that ends outside the sheet does not close it. */
    const handlePointerDown = (event: PointerEvent) => {
      if (!dismissableRef.current || event.button !== 0) return;
      if (event.target !== dialog) return;
      const content = dialog.firstElementChild ?? dialog;
      const box = content.getBoundingClientRect();
      const outside =
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom;
      if (outside) dialog.close();
    };

    dialog.addEventListener("cancel", handleNativeCancel);
    dialog.addEventListener("close", handleNativeClose);
    dialog.addEventListener("pointerdown", handlePointerDown);
    return () => {
      dialog.removeEventListener("cancel", handleNativeCancel);
      dialog.removeEventListener("close", handleNativeClose);
      dialog.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, runCancel, runClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open && dialog.isConnected) {
        dialog.showModal();
        /* showModal's focusing steps land on the first focusable descendant -
           on a Clipboard that is the close button - and when the opener was a
           keypress, :focus-visible matches, so that control paints its focus
           ring the moment the dialog appears and reads as a permanent border.
           Park the initial focus on the dialog itself instead (tabIndex makes
           it programmatically focusable without becoming a Tab stop), unless
           the consumer marked a real autofocus target. Tabbing still reaches
           every control and paints its ring normally. */
        if (!dialog.querySelector("[autofocus]")) {
          dialog.tabIndex = -1;
          dialog.focus();
        }
      }
      lockPageScroll();
      return () => unlockPageScroll();
    }

    if (dialog.open) {
      suppressNextCloseRef.current = true;
      dialog.close();
    }
    return undefined;
  }, [open]);

  return { dialogRef, handleCancel, handleClose };
}
